import { useSelector } from "react-redux"
import { selectPracticeDataStatus } from "../../domain/Practice/loaders/loader"
import { selectValidatedHealthAdvocateUser } from "../shared/selectors"
import { useEffect, useMemo, useState } from "react"
import {
  getNextIncompletePracticeStepRoute,
  getNextIncompleteProviderStepRoute,
  useDomainErrorsInSync,
} from "./helpers"
import {
  selectProvider,
  selectProviderOnboardingData,
} from "../../domain/Provider/model"
import {
  selectStatus as getTouStatus,
  TOUStatusEnum,
} from "../termsOfUse/termsOfUseSlice"
import { useQueryParams } from "./useQueryParams"
import { selectProviderDataStatus } from "../../domain/Provider/loader"
import {
  selectPractice,
  selectPracticeCompleteness,
} from "../../domain/Practice/model"
import { dataStatusesError, dataStatusesSending } from "../shared/utils"
import {
  PracticeRoleEnum,
  UserOnboardingStepsEnum,
  selectAllPracticeRoles,
  selectSiteOutageMessage,
  selectIsAffiliateRelations,
} from "../../domain/Account/userSlice"
import { selectUser } from "../shared/selectors"
import {
  appRoutes,
  getRouteWithQueryParams,
  GETTING_STARTED_ROUTE,
  ORPHANED_PROVIDER_PROMPT_ROUTE,
  SITE_OUTAGE_RESTRICTED_ROUTE,
  TASKBOARD_BASE_ROUTE,
} from "../../routes"
import {
  selectOnboardingFlowId,
  selectPracticeInvites,
} from "../../domain/Practices/selectors"
import { selectPractices } from "../../domain/Practices/selectors"
import { convertPartialPracticeToPractice } from "../../domain/Practices/helpers"
import { selectAutoSelectedPracticeResult } from "../../domain/Practices/selectors"
import { PracticeStatusEnum } from "../../domain/Practice/schema"
import { selectIsUserOnboarded } from "../../domain/Practices/selectors"
import { practiceSetupOnboardingFlowIds } from "../../domain/Practices/schema"
import { useFeatureFlagEnabled } from "posthog-js/react"
import { FeatureFlag } from "../../domain/enums/featureFlags"
import { useIsOrphanedProvider } from "../../domain/Provider/useIsOrphanedProvider"

export type LoadStatus = "initial" | "transient" | "steady"

interface ErrorState {
  practiceId: string | null
  providerId: string | null
}
interface InitialState {
  practiceIdToFetch: string | null
  providerIdToFetch: string | null
  routeTo: string | null
}
interface SteadyState {
  newPracticeId: string | null
  newProviderId: string | null
  refetchPractice: boolean
  refetchProvider: boolean
}

export interface UserDomainState {
  loadStatus: LoadStatus
  stripQueryParams: boolean
  fetchingError?: ErrorState
  initialState: InitialState
  steadyState: SteadyState
}

const initialState = {
  loadStatus: "initial" as LoadStatus,
  stripQueryParams: false,
  fetchingError: undefined,
  initialState: {
    practiceIdToFetch: null,
    providerIdToFetch: null,
    routeTo: null,
  },
  steadyState: {
    newPracticeId: null,
    newProviderId: null,
    refetchPractice: false,
    refetchProvider: false,
  },
}

/**
 * Custom hook for determining the state of initial routing/data fetching flow for the current user
 * See useNavigationOrchestrator.ts for all the useEffects associated to said flows.
 */
export const useUserDomainState = (
  readyToExecute: boolean,
  finalizedDeepLinkUrl?: string,
) => {
  const isHaUser = useSelector(selectValidatedHealthAdvocateUser)
  const siteOutageMessage = useSelector(selectSiteOutageMessage)
  const isAffiliateRelations = useSelector(selectIsAffiliateRelations)
  const isAccessRestricted =
    !!siteOutageMessage?.isEnabled &&
    !!siteOutageMessage?.isUserAccessDisabled &&
    !isAffiliateRelations
  const practice = useSelector(selectPractice)
  const autoSelectedPracticeIdResult = useSelector(
    selectAutoSelectedPracticeResult,
  )
  const practices = useSelector(selectPractices)
  const practiceInvites = useSelector(selectPracticeInvites)
  const allPracticeRoles = useSelector(selectAllPracticeRoles)
  const practiceCompleteness = useSelector(selectPracticeCompleteness)
  const provider = useSelector(selectProvider)
  const isOrphanedProviderResult = useIsOrphanedProvider(
    provider?.providerStatusDisplayId,
  )
  const practiceDataStatus = useSelector(selectPracticeDataStatus)
  const providerDataStatus = useSelector(selectProviderDataStatus)
  const touStatus = useSelector(getTouStatus)
  const user = useSelector(selectUser)
  const queryParams = useQueryParams()
  const [currentDomainState, setCurrentDomainState] =
    useState<UserDomainState>(initialState)
  const domainErrorsInSyncSync = useDomainErrorsInSync(currentDomainState)
  const userOnboarded = useSelector(selectIsUserOnboarded)
  const providerOnboardingData = useSelector(selectProviderOnboardingData)
  const getOnboardingFlowId = useSelector(selectOnboardingFlowId)
  const onboardingFlowId = getOnboardingFlowId()
  const domainFetchSending = useMemo(
    () =>
      dataStatusesSending([
        practiceDataStatus.fullPracticeDataStatus,
        providerDataStatus.baseProviderDataStatus,
        providerDataStatus.providerOnboardingDataStatus,
      ]),
    [
      practiceDataStatus.fullPracticeDataStatus,
      providerDataStatus.baseProviderDataStatus,
      providerDataStatus.providerOnboardingDataStatus,
    ],
  )
  const domainFetchError = useMemo(
    () =>
      dataStatusesError([
        practiceDataStatus.fullPracticeDataStatus,
        providerDataStatus.baseProviderDataStatus,
        providerDataStatus.providerOnboardingDataStatus,
      ]),
    [
      practiceDataStatus.fullPracticeDataStatus,
      providerDataStatus.baseProviderDataStatus,
      providerDataStatus.providerOnboardingDataStatus,
    ],
  )

  const practiceHasIdMismatchWithNoError = useMemo(
    () =>
      !!queryParams.practiceIdQueryParam &&
      !currentDomainState.fetchingError?.practiceId &&
      queryParams.practiceIdQueryParam.toLowerCase() !==
        practice.id.toLowerCase() &&
      // make sure we arent setting the state over and over again
      queryParams.practiceIdQueryParam.toLowerCase() !==
        currentDomainState.steadyState.newPracticeId?.toLowerCase(),
    [
      queryParams.practiceIdQueryParam,
      currentDomainState.fetchingError?.practiceId,
      practice.id,
      currentDomainState.steadyState.newPracticeId,
    ],
  )
  const providerHasIdMismatchWithNoError = useMemo(
    () =>
      !!queryParams.providerIdQueryParam &&
      !currentDomainState.fetchingError?.providerId &&
      queryParams.providerIdQueryParam.toLowerCase() !==
        provider.id?.toLowerCase() &&
      // make sure we arent setting the state over and over again
      queryParams.providerIdQueryParam.toLowerCase() !==
        currentDomainState.steadyState.newProviderId?.toLowerCase(),
    [
      queryParams.providerIdQueryParam,
      currentDomainState.fetchingError?.providerId,
      provider.id,
      currentDomainState.steadyState.newProviderId,
    ],
  )
  const practiceHasIdMismatchWithError = useMemo(
    () =>
      !!queryParams.practiceIdQueryParam &&
      !!currentDomainState.fetchingError?.practiceId &&
      queryParams.practiceIdQueryParam.toLowerCase() !==
        currentDomainState.fetchingError.practiceId.toLowerCase(),
    [
      queryParams.practiceIdQueryParam,
      currentDomainState.fetchingError?.practiceId,
    ],
  )
  const providerHasIdMismatchWithError = useMemo(
    () =>
      !!queryParams.providerIdQueryParam &&
      !!currentDomainState.fetchingError?.providerId &&
      queryParams.providerIdQueryParam.toLowerCase() !==
        currentDomainState.fetchingError.providerId.toLowerCase(),
    [
      queryParams.providerIdQueryParam,
      currentDomainState.fetchingError?.providerId,
    ],
  )
  const refetchPractice = useMemo(
    () => practiceHasIdMismatchWithNoError || practiceHasIdMismatchWithError,
    [practiceHasIdMismatchWithNoError, practiceHasIdMismatchWithError],
  )
  const refetchProvider = useMemo(
    () => providerHasIdMismatchWithNoError || providerHasIdMismatchWithError,
    [providerHasIdMismatchWithNoError, providerHasIdMismatchWithError],
  )

  const newErrorPracticeId = useMemo(
    () =>
      practiceDataStatus.fullPracticeDataStatus === "error"
        ? queryParams.practiceIdQueryParam
        : null,
    [
      practiceDataStatus.fullPracticeDataStatus,
      queryParams.practiceIdQueryParam,
    ],
  )
  const newErrorProviderId = useMemo(
    () =>
      providerDataStatus.baseProviderDataStatus === "error"
        ? queryParams.providerIdQueryParam
        : null,
    [
      providerDataStatus.baseProviderDataStatus,
      queryParams.providerIdQueryParam,
    ],
  )

  const newPracticeId = useMemo(
    () =>
      refetchPractice && !!queryParams.practiceIdQueryParam
        ? queryParams.practiceIdQueryParam
        : practice.id || currentDomainState.steadyState.newPracticeId,
    [
      refetchPractice,
      queryParams.practiceIdQueryParam,
      practice.id,
      currentDomainState.steadyState.newPracticeId,
    ],
  )
  const newProviderId = useMemo(
    () =>
      refetchProvider && !!queryParams.providerIdQueryParam
        ? queryParams.providerIdQueryParam
        : provider.id || currentDomainState.steadyState.newProviderId,
    [
      refetchProvider,
      queryParams.providerIdQueryParam,
      provider.id,
      currentDomainState.steadyState.newProviderId,
    ],
  )
  const steadyStateChanged = useMemo(() => {
    return (
      (!!newPracticeId &&
        newPracticeId !== currentDomainState.steadyState.newPracticeId) ||
      (!!newProviderId &&
        newProviderId !== currentDomainState.steadyState.newProviderId) ||
      refetchPractice !== currentDomainState.steadyState.refetchPractice ||
      refetchProvider !== currentDomainState.steadyState.refetchProvider
    )
  }, [
    newPracticeId,
    newProviderId,
    refetchPractice,
    refetchProvider,
    currentDomainState.steadyState.newPracticeId,
    currentDomainState.steadyState.newProviderId,
    currentDomainState.steadyState.refetchPractice,
    currentDomainState.steadyState.refetchProvider,
  ])

  const touAccepted = useMemo(
    () => touStatus === TOUStatusEnum.ACCEPTED,
    [touStatus],
  )

  const routeUserToGettingStarted = useMemo(
    () =>
      !user.completedUserOnboardingSteps ||
      !user.completedUserOnboardingSteps.includes(
        UserOnboardingStepsEnum.GETTING_STARTED,
      ),
    [user.completedUserOnboardingSteps],
  )

  const autoSelectedPractice = useMemo(() => {
    return practices.find(
      (practice) =>
        autoSelectedPracticeIdResult?.practiceId &&
        practice.id === autoSelectedPracticeIdResult.practiceId,
    )
  }, [autoSelectedPracticeIdResult?.practiceId, practices])

  const autoSelectedInvite = useMemo(() => {
    return practiceInvites.find(
      (invite) =>
        autoSelectedPracticeIdResult?.associatedInviteId &&
        invite.id === autoSelectedPracticeIdResult.associatedInviteId,
    )
  }, [autoSelectedPracticeIdResult?.associatedInviteId, practiceInvites])

  const userMustCompletePractice = useMemo(() => {
    const practiceStepRequired =
      practiceSetupOnboardingFlowIds.includes(onboardingFlowId)
    const userHasNotCompletedPracticeOnboarding =
      !user.completedUserOnboardingSteps?.includes(
        UserOnboardingStepsEnum.PRACTICE_SETUP,
      )
    return practiceStepRequired && userHasNotCompletedPracticeOnboarding
  }, [onboardingFlowId, user.completedUserOnboardingSteps])

  const nextIncompletePracticeStepRoute = useMemo(() => {
    const userHasCompletedOfficeSetup =
      user.completedUserOnboardingSteps?.includes(
        UserOnboardingStepsEnum.OFFICE_SETUP,
      ) ?? false
    const userCompletedTaxInformation =
      user.completedUserOnboardingSteps?.includes(
        UserOnboardingStepsEnum.TAX_INFORMATION,
      ) ?? false
    const userHasCompletedLiabilityInformation =
      autoSelectedPractice?.authReleaseRequired !== null &&
      (!autoSelectedPractice?.authReleaseRequired ||
        (!!autoSelectedPractice?.authRelease?.executedFileId &&
          autoSelectedPractice?.liabilityInsurances.length > 0))
    return !!autoSelectedPractice && userMustCompletePractice
      ? getNextIncompletePracticeStepRoute(
          convertPartialPracticeToPractice(autoSelectedPractice),
          practiceCompleteness,
          userHasCompletedOfficeSetup,
          userCompletedTaxInformation,
          userHasCompletedLiabilityInformation,
        )
      : null
  }, [
    user.completedUserOnboardingSteps,
    autoSelectedPractice,
    userMustCompletePractice,
    practiceCompleteness,
  ])

  const routeToOrphanedProviderPage = useMemo(() => {
    const hasPracticeLink = practices.length > 0
    const hasProviderRoleOnAnyPractice = allPracticeRoles.some(
      (practiceRoleSet) =>
        practiceRoleSet.roles.includes(PracticeRoleEnum.PROVIDER),
    )
    return (
      isOrphanedProviderResult.isOrphaned &&
      !(hasPracticeLink && !hasProviderRoleOnAnyPractice)
    )
  }, [isOrphanedProviderResult.isOrphaned, practices, allPracticeRoles])

  const userHasProvider = useMemo(() => !!provider.id, [provider.id])

  const userMustCompleteProvider = useMemo(() => {
    const userIsProvider = !!autoSelectedInvite
      ? autoSelectedInvite?.inviteRoleDisplayIds.includes(
          PracticeRoleEnum.PROVIDER,
        )
      : userHasProvider
    const userHasNotCompletedProviderOnboarding =
      !user.completedUserOnboardingSteps?.includes(
        UserOnboardingStepsEnum.PROVIDER_SETUP,
      )
    return userIsProvider && userHasNotCompletedProviderOnboarding
  }, [autoSelectedInvite, user.completedUserOnboardingSteps, userHasProvider])

  const isNoVirtualLocationEnabled = useFeatureFlagEnabled(
    FeatureFlag.IsNoVirtualLocationEnabled,
  )
  const nextIncompleteProviderStepRoute = useMemo(
    () =>
      userMustCompleteProvider
        ? getNextIncompleteProviderStepRoute(
            provider,
            providerOnboardingData,
            true,
            isNoVirtualLocationEnabled,
          )
        : null,
    [
      userMustCompleteProvider,
      provider,
      providerOnboardingData,
      isNoVirtualLocationEnabled,
    ],
  )

  const practiceIsAwaitingActivation = useMemo(() => {
    return (
      !!autoSelectedPractice &&
      !!autoSelectedPractice.status &&
      [
        PracticeStatusEnum.PENDING,
        PracticeStatusEnum.PENDING_ACTIVATION,
      ].includes(autoSelectedPractice.status as PracticeStatusEnum)
    )
  }, [autoSelectedPractice])

  const providerIsAwaitingActivation = useMemo(() => {
    return (
      !!provider.id &&
      !!provider.providerStatusDisplayId &&
      [
        PracticeStatusEnum.PENDING,
        PracticeStatusEnum.PENDING_ACTIVATION,
      ].includes(provider.providerStatusDisplayId as PracticeStatusEnum)
    )
  }, [provider])

  const routeToOnboardingActivation = useMemo(() => {
    return practiceIsAwaitingActivation || providerIsAwaitingActivation
  }, [practiceIsAwaitingActivation, providerIsAwaitingActivation])

  const allowDeepLink = useMemo(() => {
    return (
      !!finalizedDeepLinkUrl &&
      finalizedDeepLinkUrl?.split("/").filter(Boolean).length > 0 && // make sure to ignore the base route for deep linking
      !finalizedDeepLinkUrl.startsWith(SITE_OUTAGE_RESTRICTED_ROUTE) // don't deep link to outage page if no longer restricted
    )
  }, [finalizedDeepLinkUrl])

  // handle the users initial load into the app
  // this will set which flow the user needs in the useNavigationOrchestrator hook
  useEffect(() => {
    if (
      readyToExecute &&
      currentDomainState.loadStatus === "initial" &&
      !domainFetchSending &&
      !domainFetchError
    ) {
      const newInitialState = { ...currentDomainState.initialState }
      if (isAccessRestricted) {
        newInitialState.routeTo = SITE_OUTAGE_RESTRICTED_ROUTE
      } else if (!touAccepted) {
        newInitialState.routeTo = appRoutes.TERMS_OF_USE_ROUTE
      } else if (allowDeepLink) {
        newInitialState.routeTo = finalizedDeepLinkUrl!
      } else if (isHaUser) {
        newInitialState.routeTo = TASKBOARD_BASE_ROUTE
      } else if (routeToOrphanedProviderPage) {
        newInitialState.routeTo = ORPHANED_PROVIDER_PROMPT_ROUTE
      } else if (userOnboarded) {
        newInitialState.routeTo = TASKBOARD_BASE_ROUTE
        // remaining routes are associated to affiliate onboarding
      } else if (routeUserToGettingStarted) {
        newInitialState.routeTo = GETTING_STARTED_ROUTE
      } else if (!!nextIncompletePracticeStepRoute) {
        newInitialState.routeTo = nextIncompletePracticeStepRoute
      } else if (!!nextIncompleteProviderStepRoute) {
        newInitialState.routeTo = nextIncompleteProviderStepRoute
      } else if (routeToOnboardingActivation) {
        newInitialState.routeTo = appRoutes.ACTIVATION_ROUTE
      } else {
        newInitialState.routeTo = appRoutes.INTERNAL_ERROR
      }

      // combine the given route params with any deep link params
      if (newInitialState.routeTo && finalizedDeepLinkUrl) {
        const baseRoute = newInitialState.routeTo.split("?")[0]
        const existingParams = new URLSearchParams(
          newInitialState.routeTo.split("?")[1],
        )
        // Only extract the query params from finalizedDeepLinkUrl, not the full path
        const deepLinkQueryString = finalizedDeepLinkUrl.split("?")[1] || ""
        const deepLinkParams = new URLSearchParams(deepLinkQueryString)

        const combinedParams: Record<string, string> = {}
        for (const [key, value] of existingParams) {
          combinedParams[key] = value
        }
        for (const [key, value] of deepLinkParams) {
          combinedParams[key] = value
        }

        newInitialState.routeTo = getRouteWithQueryParams(
          baseRoute,
          combinedParams,
        )
      }

      const initialStateChanged = Object.keys(
        currentDomainState.initialState,
      ).some((key) => {
        return (
          currentDomainState.initialState[key as keyof InitialState] !==
          newInitialState[key as keyof InitialState]
        )
      })

      if (initialStateChanged) {
        setCurrentDomainState((prevState) => {
          const newState = {
            ...prevState,
            loadStatus: "transient" as LoadStatus,
            initialState: {
              ...newInitialState,
            },
          }
          return newState
        })
      }
    }
  }, [
    currentDomainState.initialState,
    currentDomainState.loadStatus,
    domainFetchError,
    domainFetchSending,
    isAccessRestricted,
    isHaUser,
    nextIncompletePracticeStepRoute,
    nextIncompleteProviderStepRoute,
    touAccepted,
    user.id,
    userOnboarded,
    routeUserToGettingStarted,
    routeToOnboardingActivation,
    finalizedDeepLinkUrl,
    readyToExecute,
    allowDeepLink,
    routeToOrphanedProviderPage,
  ])

  // handles error state for fetching domain
  useEffect(() => {
    if (
      readyToExecute &&
      !domainErrorsInSyncSync &&
      !domainFetchSending &&
      currentDomainState.loadStatus === "steady"
    ) {
      const newCurrentDomainState = {
        ...currentDomainState,
        stripQueryParams: domainFetchError,
        fetchingError: currentDomainState.fetchingError, // we will override this
        initialState: {
          ...currentDomainState.initialState,
        },
        steadyState: {
          ...currentDomainState.steadyState,
          refetchPractice: false,
          refetchProvider: false,
        },
      }

      // if theres no error IDs, then reset the error state, else set the error state to the new IDs
      if (!newErrorPracticeId && !newErrorProviderId) {
        newCurrentDomainState.fetchingError = undefined
      } else {
        newCurrentDomainState.fetchingError = {
          practiceId: newErrorPracticeId,
          providerId: newErrorProviderId,
        }
      }

      const steadyStateChanged = Object.keys(
        currentDomainState.steadyState,
      ).some((key) => {
        return (
          currentDomainState.steadyState[key as keyof SteadyState] !==
          newCurrentDomainState.steadyState[key as keyof SteadyState]
        )
      })

      const errorStateChanged =
        currentDomainState.fetchingError && newCurrentDomainState.fetchingError
          ? Object.keys(currentDomainState.fetchingError).some((key) => {
              return (
                currentDomainState.fetchingError?.[key as keyof ErrorState] !==
                newCurrentDomainState.fetchingError?.[key as keyof ErrorState]
              )
            })
          : currentDomainState.fetchingError !==
            newCurrentDomainState.fetchingError

      if (steadyStateChanged || errorStateChanged) {
        setCurrentDomainState(newCurrentDomainState)
      }
    }
  }, [
    currentDomainState,
    domainErrorsInSyncSync,
    domainFetchError,
    domainFetchSending,
    newErrorPracticeId,
    newErrorProviderId,
    readyToExecute,
  ])

  // handles steady-state changes
  useEffect(() => {
    if (
      !!user.id &&
      readyToExecute &&
      currentDomainState.loadStatus === "steady" &&
      domainErrorsInSyncSync &&
      !domainFetchSending &&
      steadyStateChanged
    ) {
      const newCurrentDomainState = {
        ...currentDomainState,
        stripQueryParams: false,
        fetchingError: currentDomainState.fetchingError,
        initialState: currentDomainState.initialState,
        steadyState: {
          newPracticeId: newPracticeId,
          newProviderId: newProviderId,
          refetchPractice: refetchPractice,
          refetchProvider: refetchProvider,
        },
      }

      const steadyStateChanged = Object.keys(
        currentDomainState.steadyState,
      ).some((key) => {
        return (
          currentDomainState.steadyState[key as keyof SteadyState] !==
          newCurrentDomainState.steadyState[key as keyof SteadyState]
        )
      })

      if (steadyStateChanged) {
        setCurrentDomainState(newCurrentDomainState)
      }
    }
  }, [
    currentDomainState,
    domainErrorsInSyncSync,
    domainFetchSending,
    newPracticeId,
    newProviderId,
    readyToExecute,
    refetchPractice,
    refetchProvider,
    steadyStateChanged,
    user.id,
  ])

  return {
    currentDomainState,
    setCurrentDomainState,
  }
}
